import { useTranslation } from 'react-i18next'
import { toHours } from '../utils/plans'
import type { PlanOption } from '@/@types/trackheros'

type Props = {
    plan: PlanOption
    className?: string
}

/**
 * What a plan includes, one line per limit, plus what happens at the limit.
 *
 * Shared by the billing page and onboarding so a plan never reads differently
 * in the two places somebody chooses one. Hours, not minutes: the price list
 * sells hours. Null limits are unlimited.
 *
 * The last line is not optional: it is the difference between "your bot stops
 * joining meetings" and "you get a larger bill", and a user who learns that
 * from a missed meeting will not be a user for long.
 */
const PlanLimits = ({ plan, className }: Props) => {
    const { t } = useTranslation()
    const perSeat = plan.kind === 'enterprise'

    const unlimited = (value: number | null | undefined) =>
        value == null || value <= 0

    return (
        <ul className={className}>
            <li>
                {t(
                    perSeat
                        ? 'billing.limit.hoursPerSeat'
                        : 'billing.limit.hours',
                    {
                        n: toHours(plan.minutes_included),
                    },
                )}
            </li>
            <li>
                {t(
                    perSeat && plan.storage_per_seat
                        ? 'billing.limit.storagePerSeat'
                        : 'billing.limit.storage',
                    { n: plan.recording_storage_hours_included },
                )}
            </li>
            <li>
                {unlimited(plan.calendar_limit)
                    ? t('billing.limit.calendarsUnlimited')
                    : t('billing.limit.calendars', { n: plan.calendar_limit })}
            </li>
            <li>
                {unlimited(plan.client_limit)
                    ? t('billing.limit.clientsUnlimited')
                    : t('billing.limit.clients', { n: plan.client_limit })}
            </li>
            <li>
                {unlimited(plan.chat_questions_per_month)
                    ? t('billing.limit.chatUnlimited')
                    : t('billing.limit.chat', {
                          n: plan.chat_questions_per_month,
                      })}
            </li>
            {perSeat && (plan.min_seats ?? 1) > 1 && (
                <li>{t('billing.limit.minSeats', { n: plan.min_seats })}</li>
            )}
            <li className="pt-1">
                {t(
                    plan.minutes_overage_policy === 'allow'
                        ? 'billing.overage.billed'
                        : plan.addons_enabled
                          ? 'billing.overage.stopsAddons'
                          : 'billing.overage.stops',
                )}
            </li>
        </ul>
    )
}

export default PlanLimits
